import { useState, useEffect } from 'react';
import { NavLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  LayoutDashboard,
  Settings,
  Tags,
  MapPin,
  Car,
  ClipboardList,
  FileText,
  Wrench,
  Package,
  TrendingUp,
  Building2,
  FileEdit,
  ShoppingCart,
  FileSignature,
  Wallet,
  FolderKanban,
  Users,
  ShieldCheck,
  ScrollText,
  LineChart,
  BookOpen,
  FormInput,
  MessageSquare,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  type LucideIcon,
} from 'lucide-react';

interface NavItem {
  nameKey: string;
  path: string;
  icon: LucideIcon;
}

interface NavSection {
  nameKey: string;
  icon: LucideIcon;
  items: NavItem[];
}

type NavigationItem = NavItem | NavSection;

function isNavSection(item: NavigationItem): item is NavSection {
  return 'items' in item;
}

const navigationStructure: NavigationItem[] = [
  { nameKey: 'nav.dashboard', path: '/', icon: LayoutDashboard },
  { nameKey: 'nav.equipment', path: '/equipment', icon: Settings },
  { nameKey: 'nav.categories', path: '/categories', icon: Tags },
  { nameKey: 'nav.locations', path: '/locations', icon: MapPin },
  { nameKey: 'nav.vehicles', path: '/vehicles', icon: Car },
  { nameKey: 'nav.workOrders', path: '/work-orders', icon: ClipboardList },
  { nameKey: 'nav.workRequests', path: '/work-requests', icon: FileText },
  { nameKey: 'nav.preventivePlans', path: '/preventive-plans', icon: Wrench },
  { nameKey: 'nav.articles', path: '/articles', icon: Package },
  { nameKey: 'nav.stockMovements', path: '/stock-movements', icon: TrendingUp },
  { nameKey: 'nav.suppliers', path: '/suppliers', icon: Building2 },
  { nameKey: 'nav.purchaseRequests', path: '/purchase-requests', icon: FileEdit },
  { nameKey: 'nav.purchaseOrders', path: '/purchase-orders', icon: ShoppingCart },
  { nameKey: 'nav.contracts', path: '/contracts', icon: FileSignature },
  { nameKey: 'nav.budgets', path: '/budgets', icon: Wallet },
  { nameKey: 'nav.projects', path: '/projects', icon: FolderKanban },
  { nameKey: 'messages.title', path: '/messages', icon: MessageSquare },
  {
    nameKey: 'nav.administration',
    icon: ShieldCheck,
    items: [
      { nameKey: 'nav.users', path: '/users', icon: Users },
      { nameKey: 'nav.roles', path: '/roles', icon: ShieldCheck },
      { nameKey: 'nav.activityLogs', path: '/activity-logs', icon: ScrollText },
    ],
  },
  { nameKey: 'nav.analytics', path: '/analytics', icon: LineChart },
  { nameKey: 'nav.formBuilder', path: '/form-builder', icon: FormInput },
  { nameKey: 'nav.documentation', path: '/documentation', icon: BookOpen },
];

export default function Sidebar() {
  const { t } = useTranslation();
  const [isCollapsed, setIsCollapsed] = useState(() => {
    const saved = localStorage.getItem('sidebar-collapsed');
    return saved ? JSON.parse(saved) : false;
  });

  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>(() => {
    const saved = localStorage.getItem('sidebar-sections-expanded');
    return saved ? JSON.parse(saved) : { 'nav.administration': true };
  });

  useEffect(() => {
    localStorage.setItem('sidebar-collapsed', JSON.stringify(isCollapsed));
  }, [isCollapsed]);

  useEffect(() => {
    localStorage.setItem('sidebar-sections-expanded', JSON.stringify(expandedSections));
  }, [expandedSections]);

  const toggleSidebar = () => {
    setIsCollapsed(!isCollapsed);
  };

  const toggleSection = (sectionKey: string) => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionKey]: !prev[sectionKey]
    }));
  };

  const renderNavItem = (item: NavItem, indented: boolean = false) => (
    <NavLink
      key={item.path}
      to={item.path}
      end={item.path === '/'}
      className={({ isActive }) =>
        `flex items-center ${isCollapsed ? 'justify-center px-2' : indented ? 'space-x-3 pl-11 pr-4' : 'space-x-3 px-4'} rounded-lg py-2.5 text-sm font-medium transition-colors ${
          isActive
            ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400'
            : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 hover:text-gray-900 dark:hover:text-gray-100'
        }`
      }
      title={isCollapsed ? t(item.nameKey) : undefined}
    >
      <item.icon className="h-5 w-5 flex-shrink-0" />
      {!isCollapsed && <span>{t(item.nameKey)}</span>}
    </NavLink>
  );

  const renderSection = (section: NavSection) => {
    const isExpanded = expandedSections[section.nameKey] ?? true;

    if (isCollapsed) {
      // When sidebar is collapsed, show section items in a tooltip-like manner
      return (
        <div key={section.nameKey}>
          {section.items.map(item => renderNavItem(item, false))}
        </div>
      );
    }

    return (
      <div key={section.nameKey} className="space-y-1">
        <button
          onClick={() => toggleSection(section.nameKey)}
          className="flex w-full items-center justify-between rounded-lg px-4 py-2.5 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 hover:text-gray-900 dark:hover:text-gray-100 transition-colors"
        >
          <div className="flex items-center space-x-3">
            <section.icon className="h-5 w-5 flex-shrink-0" />
            <span>{t(section.nameKey)}</span>
          </div>
          <ChevronDown
            className={`h-4 w-4 transition-transform ${isExpanded ? 'rotate-0' : '-rotate-90'}`}
          />
        </button>
        {isExpanded && (
          <div className="space-y-1">
            {section.items.map(item => renderNavItem(item, true))}
          </div>
        )}
      </div>
    );
  };

  return (
    <aside className={`flex flex-col border-r border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 transition-all duration-300 ${isCollapsed ? 'w-16' : 'w-64'}`}>
      <div className="flex h-16 items-center justify-between border-b border-gray-200 dark:border-gray-700 px-4 flex-shrink-0">
        {!isCollapsed && (
          <span className="text-lg font-bold text-blue-600 dark:text-blue-400">GMAO</span>
        )}
        <button
          onClick={toggleSidebar}
          className="rounded-lg p-1.5 text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 hover:text-gray-700 dark:hover:text-gray-300 transition-colors"
          title={isCollapsed ? t('nav.expandMenu') : t('nav.collapseMenu')}
        >
          {isCollapsed ? (
            <ChevronRight className="h-5 w-5" />
          ) : (
            <ChevronLeft className="h-5 w-5" />
          )}
        </button>
      </div>

      <nav className="flex-1 overflow-y-auto space-y-1 p-2">
        {navigationStructure.map((item) =>
          isNavSection(item) ? renderSection(item) : renderNavItem(item, false)
        )}
      </nav>
    </aside>
  );
}
